import { useCallback, useMemo } from 'react';
import {
  ALL_SIGNAL_STATUS_FILTER_VALUES,
  type SignalStatusFilterValue,
} from '@terra/shared-contracts-signals';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import {
  TraceStatusSchema,
  type TraceStatusType,
} from '@terra/shared-schemas-ai';
import {
  TraceAgentNameSchema,
  TraceGroupByFieldSchema,
  TraceSortFieldSchema,
  type TraceAgentName,
  type TraceGroupByField,
  type TraceSortField,
} from '@terra/shared-contracts-trace';
import { createTabScopedStore } from './createTabScopedStore';

export const TRACE_STATUS_FILTER_VALUES: readonly TraceStatusType[] = [
  'RUNNING',
  'PAUSED',
  'COMPLETED',
  'FAILED',
  'ABORTED',
  'BLOCKED',
];

export type TraceStatusFilter = TraceStatusType;

export type TraceResultFilter = SignalStatusFilterValue;

export type TraceAgentFilter = TraceAgentName;

const TRACE_RESULT_FILTER_VALUES = new Set<string>(
  ALL_SIGNAL_STATUS_FILTER_VALUES,
);

export type TraceListSortableColumn = TraceSortField;

export type TraceListSortOrder = 'asc' | 'desc';

export type TraceListSort = {
  colId: TraceListSortableColumn;
  sort: TraceListSortOrder;
} | null;

export type TraceListGrouping = {
  field: TraceGroupByField;
  order: TraceListSortOrder;
} | null;

export const DEFAULT_TRACE_GROUP_SORT_ORDER: TraceListSortOrder = 'asc';

export type TracesTabFilterState = {
  searchTerm: string;
  statusFilter: TraceStatusFilter[];
  resultFilter: TraceResultFilter[];
  agentFilter: TraceAgentFilter[];
  createdSince: Date | null;
  lifecycleState: LifecycleStateFilterDto;
  sort: TraceListSort;
  grouping: TraceListGrouping;
};

const useTracesTabScopedFilterStoreBase =
  createTabScopedStore<TracesTabFilterState>({
    searchTerm: '',
    statusFilter: [],
    resultFilter: [],
    agentFilter: [],
    createdSince: null,
    lifecycleState: DEFAULT_LIFECYCLE_STATE_FILTER,
    sort: null,
    grouping: null,
  });

export const isTraceListSortableColumn = (
  colId: unknown,
): colId is TraceListSortableColumn =>
  TraceSortFieldSchema.safeParse(colId).success;

export const isTraceGroupByField = (
  field: unknown,
): field is TraceGroupByField =>
  TraceGroupByFieldSchema.safeParse(field).success;

const normalizeGrouping = (value: unknown): TraceListGrouping => {
  if (value === null || typeof value !== 'object') {
    return null;
  }

  const field: unknown = Reflect.get(value, 'field');
  const order: unknown = Reflect.get(value, 'order');

  return isTraceGroupByField(field)
    ? {
        field,
        order: order === 'desc' ? 'desc' : DEFAULT_TRACE_GROUP_SORT_ORDER,
      }
    : null;
};

export const isTraceStatusFilter = (
  status: unknown,
): status is TraceStatusFilter => TraceStatusSchema.safeParse(status).success;

export const isTraceResultFilter = (
  result: unknown,
): result is TraceResultFilter =>
  typeof result === 'string' && TRACE_RESULT_FILTER_VALUES.has(result);

function normalizeStatusFilter(value: unknown): TraceStatusFilter[] {
  return Array.isArray(value) ? value.filter(isTraceStatusFilter) : [];
}

function normalizeResultFilter(value: unknown): TraceResultFilter[] {
  return Array.isArray(value) ? value.filter(isTraceResultFilter) : [];
}

export const isTraceAgentFilter = (
  agentName: unknown,
): agentName is TraceAgentFilter =>
  TraceAgentNameSchema.safeParse(agentName).success;

function normalizeAgentFilter(value: unknown): TraceAgentFilter[] {
  return Array.isArray(value) ? value.filter(isTraceAgentFilter) : [];
}

export function useTabScopedTracesFilters() {
  const { filters, update, reset, clearTabScope } =
    useTracesTabScopedFilterStoreBase();
  const statusFilter = normalizeStatusFilter(filters.statusFilter);
  const resultFilter = normalizeResultFilter(filters.resultFilter);
  const agentFilter = normalizeAgentFilter(filters.agentFilter);
  const grouping = useMemo(
    () => normalizeGrouping(filters.grouping),
    [filters.grouping],
  );

  const setSearchTerm = useCallback(
    (searchTerm: string) => update({ searchTerm }),
    [update],
  );
  const setStatusFilter = useCallback(
    (statusFilter: TraceStatusFilter[]) =>
      update({ statusFilter: normalizeStatusFilter(statusFilter) }),
    [update],
  );
  const setResultFilter = useCallback(
    (resultFilter: TraceResultFilter[]) =>
      update({ resultFilter: normalizeResultFilter(resultFilter) }),
    [update],
  );
  const setAgentFilter = useCallback(
    (agentFilter: TraceAgentFilter[]) =>
      update({ agentFilter: normalizeAgentFilter(agentFilter) }),
    [update],
  );
  const setCreatedSince = useCallback(
    (createdSince: Date | null) => update({ createdSince }),
    [update],
  );
  const setLifecycleState = useCallback(
    (lifecycleState: LifecycleStateFilterDto) => update({ lifecycleState }),
    [update],
  );
  const setSort = useCallback(
    (sort: TraceListSort) => update({ sort }),
    [update],
  );
  const setGroupByField = useCallback(
    (field: TraceGroupByField | null) =>
      update({
        grouping: field
          ? { field, order: DEFAULT_TRACE_GROUP_SORT_ORDER }
          : null,
      }),
    [update],
  );
  const setSortState = useCallback(
    ({
      sort,
      grouping: nextGrouping,
    }: {
      sort: TraceListSort;
      grouping: TraceListGrouping;
    }) => update({ sort, grouping: normalizeGrouping(nextGrouping) }),
    [update],
  );

  return {
    ...filters,
    statusFilter,
    resultFilter,
    agentFilter,
    grouping,
    setSearchTerm,
    setStatusFilter,
    setResultFilter,
    setAgentFilter,
    setCreatedSince,
    setLifecycleState,
    setSort,
    setGroupByField,
    setSortState,
    resetFilters: reset,
    clearTabScope,
  };
}
